/**
 * CodeBlock — syntax-highlighted fenced code block for chat answers.
 *
 * The LLM emits fenced blocks with a language tag (e.g. ```abap, ```sql), which
 * react-markdown passes to the `code` component as a `language-<lang>` class.
 * This renders such blocks with Prism syntax highlighting (per detected
 * language), a language label, and a copy-to-clipboard button. Inline code
 * (single backticks, no language) renders as a simple styled <code> span.
 *
 * Languages are registered explicitly (PrismLight) rather than pulling the full
 * Prism bundle, to keep the chat chunk small. ABAP/SQL are first-class because
 * SmartY answers SAP tickets; common general languages are included too.
 */

import React, { useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';

import abap from 'react-syntax-highlighter/dist/esm/languages/prism/abap';
import sql from 'react-syntax-highlighter/dist/esm/languages/prism/sql';
import python from 'react-syntax-highlighter/dist/esm/languages/prism/python';
import javascript from 'react-syntax-highlighter/dist/esm/languages/prism/javascript';
import typescript from 'react-syntax-highlighter/dist/esm/languages/prism/typescript';
import jsx from 'react-syntax-highlighter/dist/esm/languages/prism/jsx';
import tsx from 'react-syntax-highlighter/dist/esm/languages/prism/tsx';
import json from 'react-syntax-highlighter/dist/esm/languages/prism/json';
import yaml from 'react-syntax-highlighter/dist/esm/languages/prism/yaml';
import bash from 'react-syntax-highlighter/dist/esm/languages/prism/bash';
import java from 'react-syntax-highlighter/dist/esm/languages/prism/java';
import csharp from 'react-syntax-highlighter/dist/esm/languages/prism/csharp';
import markup from 'react-syntax-highlighter/dist/esm/languages/prism/markup';
import markdown from 'react-syntax-highlighter/dist/esm/languages/prism/markdown';
import c from 'react-syntax-highlighter/dist/esm/languages/prism/c';
import cpp from 'react-syntax-highlighter/dist/esm/languages/prism/cpp';
import go from 'react-syntax-highlighter/dist/esm/languages/prism/go';
import rust from 'react-syntax-highlighter/dist/esm/languages/prism/rust';
import php from 'react-syntax-highlighter/dist/esm/languages/prism/php';
import ruby from 'react-syntax-highlighter/dist/esm/languages/prism/ruby';
import css from 'react-syntax-highlighter/dist/esm/languages/prism/css';
import ini from 'react-syntax-highlighter/dist/esm/languages/prism/ini';
import toml from 'react-syntax-highlighter/dist/esm/languages/prism/toml';
import diff from 'react-syntax-highlighter/dist/esm/languages/prism/diff';
import docker from 'react-syntax-highlighter/dist/esm/languages/prism/docker';
import graphql from 'react-syntax-highlighter/dist/esm/languages/prism/graphql';
import powershell from 'react-syntax-highlighter/dist/esm/languages/prism/powershell';
import properties from 'react-syntax-highlighter/dist/esm/languages/prism/properties';
import csv from 'react-syntax-highlighter/dist/esm/languages/prism/csv';

import { log } from '../../utils/errors/logger';

// Register the supported languages once at module load.
SyntaxHighlighter.registerLanguage('abap', abap);
SyntaxHighlighter.registerLanguage('sql', sql);
SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('javascript', javascript);
SyntaxHighlighter.registerLanguage('typescript', typescript);
SyntaxHighlighter.registerLanguage('jsx', jsx);
SyntaxHighlighter.registerLanguage('tsx', tsx);
SyntaxHighlighter.registerLanguage('json', json);
SyntaxHighlighter.registerLanguage('yaml', yaml);
SyntaxHighlighter.registerLanguage('bash', bash);
SyntaxHighlighter.registerLanguage('shell', bash);
SyntaxHighlighter.registerLanguage('java', java);
SyntaxHighlighter.registerLanguage('csharp', csharp);
SyntaxHighlighter.registerLanguage('markup', markup);
SyntaxHighlighter.registerLanguage('xml', markup);
SyntaxHighlighter.registerLanguage('html', markup);
SyntaxHighlighter.registerLanguage('markdown', markdown);
SyntaxHighlighter.registerLanguage('c', c);
SyntaxHighlighter.registerLanguage('cpp', cpp);
SyntaxHighlighter.registerLanguage('go', go);
SyntaxHighlighter.registerLanguage('rust', rust);
SyntaxHighlighter.registerLanguage('php', php);
SyntaxHighlighter.registerLanguage('ruby', ruby);
SyntaxHighlighter.registerLanguage('css', css);
SyntaxHighlighter.registerLanguage('ini', ini);
SyntaxHighlighter.registerLanguage('toml', toml);
SyntaxHighlighter.registerLanguage('diff', diff);
SyntaxHighlighter.registerLanguage('docker', docker);
SyntaxHighlighter.registerLanguage('graphql', graphql);
SyntaxHighlighter.registerLanguage('powershell', powershell);
SyntaxHighlighter.registerLanguage('properties', properties);
SyntaxHighlighter.registerLanguage('csv', csv);

/** Map a few common aliases to a registered language id; '' if unknown. */
const LANGUAGE_ALIASES: Record<string, string> = {
  abap: 'abap',
  sql: 'sql',
  py: 'python',
  python: 'python',
  js: 'javascript',
  javascript: 'javascript',
  node: 'javascript',
  ts: 'typescript',
  typescript: 'typescript',
  tsx: 'tsx',
  jsx: 'jsx',
  json: 'json',
  json5: 'json',
  yaml: 'yaml',
  yml: 'yaml',
  bash: 'bash',
  sh: 'bash',
  shell: 'bash',
  zsh: 'bash',
  java: 'java',
  cs: 'csharp',
  csharp: 'csharp',
  'c#': 'csharp',
  c: 'c',
  'c++': 'cpp',
  cpp: 'cpp',
  cxx: 'cpp',
  go: 'go',
  golang: 'go',
  rust: 'rust',
  rs: 'rust',
  php: 'php',
  ruby: 'ruby',
  rb: 'ruby',
  css: 'css',
  scss: 'css',
  xml: 'xml',
  html: 'html',
  htm: 'html',
  markup: 'markup',
  markdown: 'markdown',
  md: 'markdown',
  ini: 'ini',
  cfg: 'ini',
  conf: 'ini',
  toml: 'toml',
  diff: 'diff',
  patch: 'diff',
  docker: 'docker',
  dockerfile: 'docker',
  graphql: 'graphql',
  gql: 'graphql',
  powershell: 'powershell',
  ps1: 'powershell',
  properties: 'properties',
  env: 'properties',
  csv: 'csv',
};

interface CodeBlockProps {
  /** The `language-xxx` class react-markdown puts on a fenced block, if any. */
  className?: string;
  children?: React.ReactNode;
  /** react-markdown passes inline=true for single-backtick code. */
  inline?: boolean;
}

/**
 * Heuristic: does this look like ABAP? Used as a fallback when the LLM emits a
 * code block with no language, or a generic tag like `plaintext`/`text` (which
 * it often does for SAP/ABAP snippets), so such blocks still get highlighted.
 * Conservative on purpose: requires a couple of strong ABAP signals so we never
 * mis-highlight prose or another language as ABAP.
 */
function looksLikeAbap(code: string): boolean {
  const text = code.toLowerCase();
  // Strong structural keywords / idioms that rarely co-occur outside ABAP.
  const signals = [
    /\bdata\(/, // inline data declaration: DATA(ls_x)
    /\bfield-symbol\b|<[a-z_][\w]*>/, // field symbols: <fs_x>
    /\bread table\b/,
    /\bsy-subrc\b/,
    /\bassigning\b/,
    /\bendif\.|\bendloop\.|\bendmethod\.|\bendform\./,
    /\bwith key\b/,
    /^\s*"/m, // ABAP line comment starts with a double quote
  ];
  const hits = signals.reduce((n, re) => (re.test(text) ? n + 1 : n), 0);
  return hits >= 2;
}

/** Extract the raw code text from react node children. */
function nodeToString(children: React.ReactNode): string {
  if (typeof children === 'string') {
    return children;
  }
  if (Array.isArray(children)) {
    return children.map(nodeToString).join('');
  }
  if (React.isValidElement(children)) {
    const el = children as React.ReactElement<{ children?: React.ReactNode }>;
    return nodeToString(el.props.children);
  }
  return '';
}

export const CodeBlock: React.FC<CodeBlockProps> = ({ className, children, inline }) => {
  const [copied, setCopied] = useState(false);

  const raw = nodeToString(children).replace(/\n$/, '');

  // Inline code (no language, single backtick): simple styled span.
  if (inline) {
    return (
      <code
        style={{
          backgroundColor: 'rgba(0, 48, 100, 0.07)',
          color: '#003064',
          padding: '0.1em 0.35em',
          borderRadius: '4px',
          fontSize: '0.88em',
          fontFamily: '"Roboto Mono", "SFMono-Regular", Menlo, Consolas, monospace',
        }}
      >
        {children}
      </code>
    );
  }

  // Fenced block: resolve language from the `language-xxx` class.
  const match = /language-([\w-]+)/.exec(className ?? '');
  const rawLang = (match?.[1] ?? '').toLowerCase();
  let language = LANGUAGE_ALIASES[rawLang] ?? '';

  // Fallback: the LLM frequently tags SAP/ABAP snippets as `plaintext`/`text`
  // or leaves them untagged, which would render without highlighting. When no
  // language resolved (unknown, generic, or missing tag), detect ABAP from the
  // code itself so answers still highlight. Only ABAP gets this treatment -- it
  // is the one language SmartY answers consistently emit unlabelled.
  const isGenericOrMissing = rawLang === '' || rawLang === 'plaintext' || rawLang === 'text';
  if (language === '' && isGenericOrMissing && looksLikeAbap(raw)) {
    language = 'abap';
  }

  const handleCopy = () => {
    void navigator.clipboard
      .writeText(raw)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch((err) => log.warn('CodeBlock copy failed', { err: String(err) }));
  };

  return (
    <Box
      sx={{
        position: 'relative',
        my: 1,
        borderRadius: '8px',
        border: '1px solid #e3e8ef',
        overflow: 'hidden',
        backgroundColor: '#fafbfc',
      }}
    >
      {/* Header: language label + copy button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: '5px',
          borderBottom: '1px solid #eaecf0',
          backgroundColor: '#f5f7fa',
        }}
      >
        <Box
          component="span"
          sx={{
            fontSize: '0.68rem',
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: '#8a97a6',
            fontFamily: '"Roboto", sans-serif',
          }}
        >
          {language || rawLang || 'text'}
        </Box>
        <Tooltip title={copied ? 'Kopiert' : 'Kopieren'} placement="left">
          <IconButton
            size="small"
            onClick={handleCopy}
            aria-label="Code kopieren"
            sx={{ color: copied ? '#1e7e34' : '#003064', p: '3px' }}
          >
            {copied ? (
              <CheckIcon sx={{ fontSize: '0.95rem' }} />
            ) : (
              <ContentCopyIcon sx={{ fontSize: '0.95rem' }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* No wrapLongLines: keep white-space:pre so indentation/leading
          whitespace is preserved exactly (wrap would switch to pre-wrap and
          can alter indentation). Long lines scroll horizontally instead, the
          standard behavior for code (as in IDEs / GitHub). */}
      <Box sx={{ overflowX: 'auto' }}>
        <SyntaxHighlighter
          language={language || undefined}
          style={oneLight}
          customStyle={{
            margin: 0,
            padding: '12px 14px',
            background: 'transparent',
            fontSize: '0.82rem',
            lineHeight: 1.5,
          }}
          codeTagProps={{
            style: {
              fontFamily: '"Roboto Mono", "SFMono-Regular", Menlo, Consolas, monospace',
              whiteSpace: 'pre',
            },
          }}
        >
          {raw}
        </SyntaxHighlighter>
      </Box>
    </Box>
  );
};
